import { useEffect, useState } from "react";
import {
  listMyCrew,
  inviteCrewMember,
  removeCrewMember,
  ApiError,
  type AdminAssignment,
  type CrewMember,
} from "../api";
import { AmenitiesEditor } from "./AmenitiesEditor";
import { BannerUploader } from "./BannerUploader";

/**
 * Trail details tool — metadata, amenities, and (for owners) crew.
 *
 * Everything that's NOT the daily status push or the events calendar
 * lives here. Read-only metadata at the top (region/state/status page
 * URL), then editable amenities, then crew management for owners.
 */
export function AdminTrailDetails({
  trail,
}: {
  trail: AdminAssignment;
}) {
  return (
    <>
      <div className="admin-section">
        <div className="admin-section-heading">Location</div>
        <div className="admin-section-body">
          <dl className="admin-detail-list">
            <div>
              <dt>Region</dt>
              <dd>{trail.region || <span className="muted">Not set</span>}</dd>
            </div>
            <div>
              <dt>State</dt>
              <dd>{trail.state || <span className="muted">Not set</span>}</dd>
            </div>
            <div>
              <dt>Country</dt>
              <dd>{trail.country || <span className="muted">Not set</span>}</dd>
            </div>
            <div>
              <dt>Status page</dt>
              <dd>
                {trail.statusPageUrl ? (
                  <a
                    href={trail.statusPageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mono"
                    style={{ fontSize: 12 }}
                  >
                    {trail.statusPageUrl}
                  </a>
                ) : (
                  <span className="muted">No URL set</span>
                )}
              </dd>
            </div>
          </dl>
          <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            Region, state, and the status-page URL are set when the trail
            is first claimed. Reach out to a curator if these need to change.
          </p>
        </div>
      </div>

      <BannerUploader slug={trail.slug} />

      <div className="admin-section">
        <div className="admin-section-heading">Amenities</div>
        <div className="admin-section-body">
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            What's available at the trailhead. Riders see these on the
            trail detail screen.
          </p>
          <AmenitiesEditor slug={trail.slug} />
        </div>
      </div>

      {trail.role === "owner" && (
        <CrewSection slug={trail.slug} trailName={trail.name} />
      )}
    </>
  );
}

function CrewSection({
  slug,
  trailName,
}: {
  slug: string;
  trailName: string;
}) {
  const [crew, setCrew] = useState<CrewMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const r = await listMyCrew(slug);
      setCrew(r.items);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't load the crew."
      );
      setCrew([]);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function handleInvite() {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviting(true);
    setError(null);
    setNotice(null);
    try {
      const r = await inviteCrewMember(slug, email, "steward");
      setInviteEmail("");
      setNotice(
        r.alreadyOnCrew
          ? `${r.email} is already on the crew.`
          : `${r.email} added as Crew.`
      );
      reload();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't add to the crew."
      );
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(member: CrewMember) {
    if (member.isYou) return;
    if (
      !confirm(
        `Remove ${member.email} from the ${trailName} crew? They'll lose access to set status.`
      )
    )
      return;
    setRemovingId(member.userId);
    setError(null);
    setNotice(null);
    try {
      await removeCrewMember(slug, member.userId);
      setNotice(`Removed ${member.email}.`);
      reload();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't remove that member."
      );
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section-heading">Crew</div>
      <div className="admin-section-body">
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          People who can set status and post updates for this trail. Add a
          rider to your crew by email — they need to have signed in once.
        </p>

        {error && (
          <div className="admin-feedback admin-feedback-err">{error}</div>
        )}
        {notice && (
          <div className="admin-feedback admin-feedback-ok">{notice}</div>
        )}

        {crew === null ? (
          <div className="muted" style={{ fontSize: 13 }}>
            Loading the crew…
          </div>
        ) : crew.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            No crew yet. You're flying solo.
          </div>
        ) : (
          <ul className="crew-list" role="list">
            {crew.map((m) => (
              <li key={m.userId} className="crew-row">
                <div className="crew-row-main">
                  <div className="crew-row-email">
                    {m.email}
                    {m.isYou && (
                      <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                        (you)
                      </span>
                    )}
                  </div>
                  <div className="crew-row-meta muted">
                    {m.role === "owner" ? "Trail Boss" : m.role === "steward" ? "Crew" : "Rider"}
                    {m.joinedAt
                      ? ` · joined ${new Date(m.joinedAt * 1000).toLocaleDateString()}`
                      : ""}
                  </div>
                </div>
                {!m.isYou && m.role !== "owner" && (
                  <button
                    type="button"
                    className="small secondary"
                    disabled={removingId === m.userId}
                    onClick={() => handleRemove(m)}
                  >
                    {removingId === m.userId ? "…" : "Remove"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="crew-grant">
          <input
            type="email"
            placeholder="rider@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleInvite();
              }
            }}
            disabled={inviting}
          />
          <button
            type="button"
            onClick={handleInvite}
            disabled={inviting || !inviteEmail.trim()}
          >
            {inviting ? "Adding…" : "Add to crew"}
          </button>
        </div>
      </div>
    </div>
  );
}
